import ModelInterface from 'src/models/ModelInterface';
import {RawMailing, IVote} from '@/types/entity/RawMailing';
import store from '@/store/index';
import Vue from 'vue';
import {DateTime} from 'luxon';
import {
  MAILING_STATUS_COMPLETED,
  MAILING_STATUS_DRAFT,
  MAILING_STATUS_PROGRESS,
  MAILING_STATUS_READY,
  MAILING_STATUS_BEFORE_PROGRESS,
  MAILING_STATUS_CANCELED,
  MAILING_TYPE_TEXT,
  MAILING_TYPE_NEWS,
  VOTE_TYPE_STARS5,
  VOTE_TYPE_YESNO,
  VOTE_TYPE_LIKE
} from '@/constants';

// @ts-ignore
import messages from '@/lang/index';

interface IStatuses {
  [key: string]: {label: string, bgColor: string, color: string, borderColor?: string};
}

const MAILING_WEIRD_TIMEOUT = 10800000

const STATUSES: IStatuses = {
  [MAILING_STATUS_DRAFT]: {label: messages.common.statuses.draft, bgColor: '#f4f4f5', color: '#909399'},
  [MAILING_STATUS_READY]: {label: messages.common.statuses.ready, bgColor: '#f4f4f5', color: '#909399'},
  [MAILING_STATUS_BEFORE_PROGRESS]: {label: messages.common.statuses.before_progress, bgColor: '#FEEDC2', color: '#E6AB45'},
  [MAILING_STATUS_PROGRESS]: {label: messages.common.statuses.progres, bgColor: '#FEEDC2', color: '#E6AB45'},
  [MAILING_STATUS_COMPLETED]: {label: messages.common.statuses.completed, bgColor: '#DFF3DD', color: '#15BF51'},
  [MAILING_STATUS_CANCELED]: {label: messages.common.statuses.canceled, bgColor: '#FFD9D9', color: '#FF3738'}
};

const MAILLING_TYPES: IStatuses = {
  [MAILING_TYPE_TEXT]: {label: messages.newsletters.types.text, bgColor: '#f4f4f5', color: '#909399'},
  [MAILING_TYPE_NEWS]: {label: messages.newsletters.types.news, bgColor: '#f4f4f5', color: '#909399'},
};


class Mailing extends ModelInterface<RawMailing> {
  constructor (data: RawMailing) {
    super(data);
  }

  public updateData () {
    store.dispatch('mailings/get', this.getId())
  }

  public getTitle () {
    return this.data.title || '';
  }

  public getText () {
    return this.data.text || '';
  }

  public getStatus () {
    if (this.getSendAt() && this.getSendAt()! < DateTime.local().toMillis() && this.data.status === MAILING_STATUS_READY) {
      return MAILING_STATUS_BEFORE_PROGRESS;
    } else {
      return this.data.status;
    }
  }

  public getSendAt () {
    return this.data.send_at;
  }

  public getFilter () {
    return this.data.filter || {};
  }

  public getCreatedFirstName () {
    return this.data.created && this.data.created.first_name || '';
  }

  public getCreatedLastName () {
    return this.data.created && this.data.created.last_name || '';
  }

  public getCreatedMiddleName () {
    return this.data.created && this.data.created.middle_name || '';
  }

  public getCreatedFullName () {
    const fullName = (
      this.getCreatedLastName() +
      ' ' +
      this.getCreatedFirstName() +
      ' ' +
      this.getCreatedMiddleName()
    )
    return fullName.trim() ? fullName : messages.newsletters.automate;
  }

  public getCreatedId () {
    return this.data.created.id;
  }

  public getRcptNum () {
    return this.data.rcpt_num;
  }
  public getMsgrNum () {
    return this.data.msgr_num || 0;
  }

  public getSentErr () {
    return this.data.sent_err;
  }

  public getSentOk () {
    return this.data.sent_ok;
  }

  public getSendStats () {
    return this.data.send_stats
  }

  public getMsgsNum () {
    let num = 0
    if (this.getSendStats()) {
      for (const ok in this.getSendStats()) {
        if (this.getSendStats()!.hasOwnProperty(ok)) {
          num += this.getSendStats()![ok].ok;
        }
      }
    }
    return num
  }

  public getSendProgress (): number {
    let progress = ((this.getSentOk() + this.getSentErr()) / this.getRcptNum()) * 100;
    return Math.round(progress);
  }

  public getSendAtDate (format?: string): string {
    if (this.getSendAt()) {
      let date = DateTime.fromMillis(this.getSendAt() as number);
      return date.toFormat(format || 'dd.LL.yyyy H:mm');
    } else {
      return messages.labels.not_specified;
    }
  }

  public getCtDate (format?: string): string {
    if (this.getCt()) {
      let date = DateTime.fromMillis(this.getCt() as number);
      return date.toFormat(format || 'dd.LL.yyyy H:mm');
    } else {
      return messages.labels.not_specified;
    }
  }

  public getStatusLabel (status?: string) {
    if (status) {
      return STATUSES[status].label || messages.labels.unknown;
    } else {
      return STATUSES[this.getStatus()].label;
    }
  }

  public getStatusColor (status?: string) {
    if (status) {
      return STATUSES[status].color || '#000';
    } else {
      return STATUSES[this.getStatus()].color;
    }
  }

  public getStatusBgColor (status?: string) {
    if (status) {
      return STATUSES[status].bgColor || '#000';
    } else {
      return STATUSES[this.getStatus()].bgColor;
    }
  }

  public getType () {
    return this.data.type || ''
  }

  public getTypeLabel (type?: string) {
    if (type) {
      return MAILLING_TYPES[type].label || messages.labels.unknown;
    } else {
      return MAILLING_TYPES[this.getType()].label;
    }
  }

  public getTypeColor (type?: string) {
    if (type) {
      return MAILLING_TYPES[type].color || '#000';
    } else {
      return MAILLING_TYPES[this.getType()].color;
    }
  }

  public getTypeBgColor (type?: string) {
    if (type) {
      return MAILLING_TYPES[type].bgColor || '#000';
    } else {
      return MAILLING_TYPES[this.getType()].bgColor;
    }
  }

  public getVote () {
    return this.data.vote || false;
  }

  public getRating () {
    if (this.data.vote && this.data.vote.type === VOTE_TYPE_STARS5) {
      let total = 0;
      let summ = 0;
      for (const key in this.data.vote.ans) {
        total += this.data.vote.ans[key];
        summ += Number(key) * this.data.vote.ans[key]
      }
      return {value: summ && Number((summ / total).toFixed(1)), total}
    } else {
      return false;
    }
  }

  public getYesNo () {
    if (this.data.vote && this.data.vote.type === VOTE_TYPE_YESNO) {
      let total = 0;
      for (const key in this.data.vote.ans) {
        total += this.data.vote.ans[key];
      }
      return {value: this.data.vote.ans || {yes: 0, no: 0}, total}
    } else {
      return false;
    }
  }

  public getLikes () {
    if (this.data.vote && this.data.vote.type === VOTE_TYPE_LIKE) {
      return {value: this.data.vote.ans && this.data.vote.ans.like || 0}
    } else {
      return false;
    }
  }


  public getCt () {
    return this.data.ct;
  }

  public getTs () {
    return this.data.ts;
  }

  public isWeird (): boolean {
    if (
      this.getStatus() === MAILING_STATUS_PROGRESS &&
      (Date.now() - this.getTs()) > MAILING_WEIRD_TIMEOUT
    ) {
      return true
    }
    return false
  }

  public isWeird2 (): boolean {
    if (
      this.getStatus() === MAILING_STATUS_READY &&
      (Date.now() - this.getSendAt()) > MAILING_WEIRD_TIMEOUT
    ) {
      return true
    }
    return false
  }

  public isAutosent (): boolean {
    return !!this.getFilter().for_all
  }

  get title () {
    return this.getTitle();
  }

  get text () {
    return this.getText();
  }

  get rcpt_num () {
    return this.getRcptNum();
  }

  get sent_ok () {
    return this.getSentOk();
  }

  get sent_err () {
    return this.getSentErr();
  }

  get date () {
    return this.getSendAtDate();
  }
}

export default Mailing;